import React, { useState, useEffect, useRef } from "react";
import { Search, ChevronDown } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import "./SearchableSelect.css";

export default function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  noOptionsText,
  disabled = false,
}) {
  const { isRTL } = useLanguage();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);

  const defaultPlaceholder = placeholder || (isRTL ? "اختر من القائمة" : "Select an option");
  const defaultSearchPlaceholder = searchPlaceholder || (isRTL ? "ابحث هنا..." : "Search...");
  const defaultNoOptions = noOptionsText || (isRTL ? "لا توجد نتائج مطابقة" : "No options found");

  // Sync display with selected value
  const selectedOption = options.find((o) => o.id === value);
  const displayValue = selectedOption
    ? (isRTL && selectedOption.nameAr ? selectedOption.nameAr : (selectedOption.nameAr || selectedOption.name))
    : "";

  useEffect(() => {
    if (!open) {
      setQuery("");
    }
  }, [open]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options.filter((o) => {
    if (!query) return true;
    const q = query.toLowerCase();
    const name = (o.name || "").toLowerCase();
    const nameAr = (o.nameAr || "").toLowerCase();
    return name.includes(q) || nameAr.includes(q);
  }).sort((a, b) => {
    if (!query) return 0;
    const q = query.toLowerCase();
    const aName = (a.name || "").toLowerCase();
    const aNameAr = (a.nameAr || "").toLowerCase();
    const bName = (b.name || "").toLowerCase();
    const bNameAr = (b.nameAr || "").toLowerCase();
    
    const aStarts = aName.startsWith(q) || aNameAr.startsWith(q);
    const bStarts = bName.startsWith(q) || bNameAr.startsWith(q);
    
    if (aStarts && !bStarts) return -1;
    if (!aStarts && bStarts) return 1;
    return 0;
  });

  return (
    <div className={`searchable-select ${disabled ? "disabled" : ""}`} ref={wrapperRef}>
      <div 
        className="searchable-select__control" 
        onClick={() => !disabled && setOpen(!open)}
      >
        <span className="searchable-select__value">
          {displayValue || <span className="searchable-select__placeholder">{defaultPlaceholder}</span>}
        </span>
        <ChevronDown size={16} className="searchable-select__icon" />
      </div>

      {open && (
        <div className="searchable-select__menu">
          <div className="searchable-select__search-wrapper">
            <Search size={14} className="searchable-select__search-icon" />
            <input
              type="text"
              autoFocus
              className="searchable-select__search-input"
              placeholder={defaultSearchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <ul className="searchable-select__list">
            {filteredOptions.length === 0 ? (
              <li className="searchable-select__no-options">{defaultNoOptions}</li>
            ) : (
              filteredOptions.map((o) => (
                <li
                  key={o.id}
                  className={`searchable-select__option ${o.id === value ? "selected" : ""}`}
                  onClick={() => {
                    onChange(o.id);
                    setOpen(false);
                  }}
                >
                  <div className="searchable-select__option-text">
                    <span style={{ fontWeight: 500 }}>{isRTL && o.nameAr ? o.nameAr : (o.name || o.nameAr)}</span>
                    {(o.nameAr && o.name && o.nameAr !== o.name) && (
                      <span className="searchable-select__option-sub" style={{ marginInlineStart: "6px", color: "var(--text-secondary)", fontSize: "0.85em" }}>
                        ({isRTL ? o.name : (o.nameAr)})
                      </span>
                    )}
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
